/**
 * Media Service Contracts
 *
 * Zod schemas for Media Service (image upload) inter-service communication.
 * Frontend → Media Service (Port 30003)
 * Backend → Media Service (internal verification)
 *
 * Source of truth:
 * - services/media-service/src/routes/upload.ts (endpoint definitions)
 * - frontend/src/services/api.ts (mediaAPI client)
 */
import { z } from 'zod';
// =============================================================================
// UPLOAD TYPES
// =============================================================================
/**
 * Allowed upload types
 */
export const mediaUploadTypeValues = ['deposit', 'withdrawal', 'payout'];
export const mediaUploadTypeSchema = z.enum(mediaUploadTypeValues);
// =============================================================================
// UPLOAD
// POST /upload (multipart/form-data)
// =============================================================================
/**
 * Upload request body fields (in addition to file)
 * Note: This is multipart/form-data, not JSON
 */
export const uploadRequestBodySchema = z.object({
    type: mediaUploadTypeSchema,
    transactionId: z.string().optional(), // Reference number to associate with
});
/**
 * Upload response on success
 */
export const uploadSuccessResponseSchema = z.object({
    success: z.literal(true),
    fileId: z.string(), // Unique file identifier
    url: z.string().url(), // Full URL to access the file
    size: z.number(), // Compressed file size in bytes
    dimensions: z.object({
        width: z.number(),
        height: z.number(),
    }),
    compressionRatio: z.number(), // Percentage reduced (0-100)
});
/**
 * Upload error response
 */
export const uploadErrorResponseSchema = z.object({
    error: z.string(),
    detected: z.string().optional(), // Detected MIME type (for invalid file errors)
});
/**
 * Combined upload response
 */
export const uploadResponseSchema = z.union([
    uploadSuccessResponseSchema,
    uploadErrorResponseSchema,
]);
// =============================================================================
// FILE ID FORMAT
// =============================================================================
/**
 * File ID format: {type}_{telegramId}_{transactionId?}_{timestamp}
 * Examples:
 * - deposit_123456789_42_1703894400000
 * - withdrawal_987654321_1703894500000
 */
export const fileIdSchema = z.string().min(10, 'Invalid fileId format');
// =============================================================================
// MEDIA SERVING
// GET /media/:fileId
// =============================================================================
/**
 * Media serving doesn't return JSON - it returns the image binary.
 * These schemas are for error responses only.
 */
export const mediaNotFoundErrorSchema = z.object({
    error: z.literal('File not found'),
});
export const mediaInvalidFormatErrorSchema = z.object({
    error: z.literal('Invalid fileId format'),
});
// =============================================================================
// INTERNAL VERIFICATION
// GET /internal/verify/:fileId
// =============================================================================
export const verifyFileResponseSchema = z.object({
    exists: z.boolean(),
    fileId: z.string(),
    url: z.string().url().nullable(), // Full URL if exists, null otherwise
});
export const verifyFileErrorResponseSchema = z.object({
    exists: z.literal(false),
    error: z.string(),
});
// =============================================================================
// HEALTH CHECK
// GET /health
// =============================================================================
export const mediaHealthCheckResponseSchema = z.object({
    status: z.literal('ok'),
    service: z.literal('media-service'),
    timestamp: z.string(),
    config: z.object({
        maxFileSize: z.string(), // e.g., "10MB"
        maxWidth: z.number(),
        jpegQuality: z.number(),
    }),
});
// =============================================================================
// FRONTEND API HELPERS
// Types matching frontend/src/services/api.ts mediaAPI
// =============================================================================
/**
 * Upload function parameters (frontend API)
 */
export const mediaUploadParamsSchema = z.object({
    file: z.instanceof(File).or(z.instanceof(Blob)),
    type: mediaUploadTypeSchema,
    transactionId: z.string().optional(),
});
/**
 * Result returned to frontend after upload
 */
export const mediaUploadResultSchema = z.object({
    fileId: z.string(),
    url: z.string().url(),
});
// =============================================================================
// MEDIA URL RESOLVER
// Helps frontend resolve photoUrl from backend (could be fileId or full URL)
// =============================================================================
/**
 * Check if a string is already a full URL
 */
export function isFullUrl(value) {
    return value.startsWith('http://') || value.startsWith('https://');
}
/**
 * Resolve a photo URL from backend.
 * Backend stores either:
 * - Full URL (from media service upload response)
 * - Just a fileId (legacy or compact storage)
 *
 * @param photoUrl - The photoUrl from backend (could be full URL or fileId)
 * @param mediaServiceUrl - Base URL of media service (e.g., from VITE_MEDIA_SERVICE_URL)
 * @returns Full URL to access the photo
 */
export function resolveMediaUrl(photoUrl, mediaServiceUrl) {
    if (!photoUrl)
        return null;
    // Already a full URL - return as-is
    if (isFullUrl(photoUrl)) {
        return photoUrl;
    }
    // It's a fileId - construct full URL
    return `${mediaServiceUrl}/media/${photoUrl}`;
}
/**
 * Schema for validating photoUrl field (nullable string)
 * Use this in response schemas that include photo URLs
 */
export const photoUrlSchema = z.string().nullable();
//# sourceMappingURL=media.js.map